/**
 * @fileoverview Presentational body of the deliverable drawer: project/lesson
 * bar, instructions, teacher files, previous submission, the legacy upload
 * widgets (FileUpload + GDriveWrapper) and links (AR-678: "Add link" button
 * beside the upload opens `AddLinkModal`; submitted links show under
 * "Files and links:" with Submitted at, Late badge and removal). Deliverables
 * have no free text — the answer field was removed (AR-780); a default
 * description is sent on submit. The parent drawer owns load/submit state,
 * the link callbacks/toasts and the fixed footer. Defensive throughout.
 */

import { useState } from 'react'
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Center,
  Group,
  Paper,
  Stack,
  Text,
  TypographyStylesProvider,
} from '@mantine/core'
import { IconFile, IconLink, IconTrash } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import FileUpload from '@legacy/common/_components/FileUpload'
import { ClassroomAttachmentLink, StudentClassroomPicker } from 'components/ClassroomDeliverable'
import { GoogleClassroomLogo } from 'components/GoogleClassroomLogo'
import { useClassroomIntegrationEnabled } from 'integrations/classroom'
import { DeliverableDrawerSkeleton } from '../components'
import { LIGHT_FILE_BUTTONS_SX } from '../styles'
import { AddLinkModal } from './AddLinkModal'
import { canSubmitDeliverable } from './deliverableForm'
import { formatLinkSubmittedAt, isLinkLate, type AddLinkOutcome } from './linkUtils'
import type { DeliverableDetail, DeliveryLink, TaskFile } from '../types'

interface StudentDeliverableSectionProps {
  loading: boolean
  errored: boolean
  data: DeliverableDetail | null
  links: readonly DeliveryLink[]
  removingLinkIds: ReadonlySet<string>
  onAutoSubmit: () => void
  onAddLink: (url: string) => Promise<AddLinkOutcome>
  onRemoveLink: (linkId: string) => void
}

const ERROR_FALLBACK_HEIGHT = 160

const LabeledValue = ({ label, value }: { label: string; value: string }) => (
  <Text size="sm">
    <Text span color="dimmed">{label}: </Text>
    <Text span weight={500}>{value}</Text>
  </Text>
)

const FileLink = ({ file }: { file: TaskFile }) => (
  <Anchor href={file.downloadUrl} target="_blank" rel="noopener noreferrer" size="sm">
    <Group spacing={6} noWrap>
      <IconFile size={14} />
      <Text size="sm" lineClamp={1}>{file.name}</Text>
    </Group>
  </Anchor>
)

interface SubmittedLinkItemProps {
  link: DeliveryLink
  late: boolean
  submittedAtLabel: string
  lateLabel: string
  formattedSubmittedAt: string
  removing: boolean
  onRemove: (linkId: string) => void
}

// Submitted link row: full clickable URL (breaking anywhere so it never
// overflows the drawer), "Submitted at" + Late badge and removal.
const SubmittedLinkItem = ({
  link,
  late,
  submittedAtLabel,
  lateLabel,
  formattedSubmittedAt,
  removing,
  onRemove,
}: SubmittedLinkItemProps) => (
  <Paper withBorder radius="sm" p="xs">
    <Stack spacing={4}>
      <Group spacing="xs" position="apart" noWrap>
        <Anchor
          href={link.url}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          sx={{ flex: 1, minWidth: 0, wordBreak: 'break-all' }}
        >
          <Group spacing={6} noWrap align="flex-start">
            <Box sx={{ flexShrink: 0 }}><IconLink size={16} /></Box>
            <Text size="sm" sx={{ wordBreak: 'break-all' }}>{link.url}</Text>
          </Group>
        </Anchor>
        <ActionIcon
          variant="subtle"
          color="red"
          loading={removing}
          onClick={() => onRemove(link.id)}
          aria-label={`remove-link-${link.id}`}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </Group>
      {formattedSubmittedAt && (
        <Group spacing={6} align="center">
          <Text size="xs" color="dimmed">{`${submittedAtLabel}: ${formattedSubmittedAt}`}</Text>
          {late && <Badge color="red" size="sm">{lateLabel}</Badge>}
        </Group>
      )}
    </Stack>
  </Paper>
)

export function StudentDeliverableSection({
  loading,
  errored,
  data,
  links,
  removingLinkIds,
  onAutoSubmit,
  onAddLink,
  onRemoveLink,
}: StudentDeliverableSectionProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || 'pt-BR'
  const tr = (key: string) => t(`student_tasks.deliverable_drawer.${key}`)
  // AR-670: gates the "Add from Google Classroom" affordance (school flag).
  const { enabled: classroomEnabled } = useClassroomIntegrationEnabled()

  const [linkModalOpened, setLinkModalOpened] = useState(false)
  const [classroomPickerOpened, setClassroomPickerOpened] = useState(false)

  if (loading) return <DeliverableDrawerSkeleton />
  if (errored || !data) {
    return (
      <Center style={{ height: ERROR_FALLBACK_HEIGHT }}>
        <Text color="dimmed" size="sm">{tr('error_loading')}</Text>
      </Center>
    )
  }

  const showForm = canSubmitDeliverable(data)
  const filesPath = `/projects/${data.projectId}/student_task_deliveries/${data.studentTaskDeliveryId}`

  return (
    <Stack spacing="lg">
      <Paper withBorder radius="sm" p="md">
        <Group position="apart" align="flex-start" noWrap>
          <Stack spacing={2}>
            <LabeledValue label={tr('project_label')} value={data.projectName} />
            <LabeledValue label={tr('lesson_label')} value={data.lessonTitle} />
          </Stack>
          {data.tutor && (
            <Box style={{ flexShrink: 0 }}>
              <LabeledValue label={tr('created_by')} value={data.tutor} />
            </Box>
          )}
        </Group>
      </Paper>

      <Stack spacing="xs">
        <Group spacing="xs" align="center">
          <Text size="md" weight={700}>{data.title}</Text>
          {data.done && <Badge color="green" size="sm">{tr('submitted_badge')}</Badge>}
          {data.overdue && !data.done && <Badge color="red" size="sm">{tr('late_badge')}</Badge>}
        </Group>
        {data.deadline && <LabeledValue label={tr('due_date')} value={data.deadline} />}
        {data.description && (
          <div>
            <Text size="sm" color="dimmed">{tr('instructions')}:</Text>
            <TypographyStylesProvider sx={{ fontSize: '0.875rem' }}>
              <div dangerouslySetInnerHTML={{ __html: data.description }} />
            </TypographyStylesProvider>
          </div>
        )}
      </Stack>

      {data.taskFiles.length > 0 && (
        <Stack spacing="xs">
          <Text size="sm" color="dimmed">{tr('task_files')}:</Text>
          {data.taskFiles.map(file => <FileLink key={file.id} file={file} />)}
        </Stack>
      )}

      {data.lastDelivery && (
        <Paper withBorder radius="sm" p="md">
          <Stack spacing="xs">
            <Group spacing="xs" align="center">
              <Text size="sm" weight={600}>{tr('your_submission')}</Text>
              {data.lastDelivery.createdAt && (
                <Text size="xs" color="dimmed">{data.lastDelivery.createdAt}</Text>
              )}
            </Group>
            {data.lastDelivery.description && (
              <TypographyStylesProvider sx={{ fontSize: '0.875rem' }}>
                <div dangerouslySetInnerHTML={{ __html: data.lastDelivery.description }} />
              </TypographyStylesProvider>
            )}
          </Stack>
        </Paper>
      )}

      {showForm && (
        <Stack spacing="sm">
          <Text size="sm" weight={600}>{tr('your_submission')}</Text>
          <Group spacing="sm" align="flex-start" noWrap>
            <Box sx={{ ...LIGHT_FILE_BUTTONS_SX, flex: 1, minWidth: 0 }}>
              <div className="fileContainer" key={data.studentTaskDeliveryId}>
                <FileUpload
                  uploadUrl={`${filesPath}/planning_files`}
                  fetchFilesUrl={`${filesPath}/planning_files.json`}
                  deleteFilesUrl={`${filesPath}/planning_files`}
                  typeFile="planning_files"
                  fileUploadId="student_task_deliveries"
                  appliedFile={false}
                  successUploadCallback={onAutoSubmit}
                />
              </div>
            </Box>
            {/* AR-670: manual pick of the student's own Classroom submission
                attachments — complements the automatic import (cron), which
                keeps mirroring into this same list. */}
            {classroomEnabled && (
              <Button
                variant="outline"
                leftIcon={<GoogleClassroomLogo size={14} />}
                onClick={() => setClassroomPickerOpened(true)}
                disabled={!data.studentTaskDeliveryId}
              >
                {t('classroom.addFromGoogleClassroom')}
              </Button>
            )}
            <Button
              variant="outline"
              leftIcon={<IconLink size={14} />}
              onClick={() => setLinkModalOpened(true)}
              disabled={!data.studentTaskDeliveryId}
            >
              {tr('add_link')}
            </Button>
          </Group>

          {(links.length > 0 || data.googleDriveFiles.length > 0) && (
            <Stack spacing="xs">
              <Text size="sm" weight={600}>{tr('files_and_links')}</Text>
              {/* AR-876: anexos do Drive espelhados do Classroom (AR-805). O
                  link é a única cópia quando o download falhou/foi pulado. */}
              {data.googleDriveFiles.map(driveFile => (
                <ClassroomAttachmentLink key={driveFile.id} file={driveFile} />
              ))}
              {links.map(link => (
                <SubmittedLinkItem
                  key={link.id}
                  link={link}
                  late={isLinkLate(link.createdAt, data.deadline)}
                  submittedAtLabel={tr('submitted_at')}
                  lateLabel={tr('late_badge')}
                  formattedSubmittedAt={formatLinkSubmittedAt(link.createdAt, locale)}
                  removing={removingLinkIds.has(link.id)}
                  onRemove={onRemoveLink}
                />
              ))}
            </Stack>
          )}

          <AddLinkModal
            opened={linkModalOpened}
            onClose={() => setLinkModalOpened(false)}
            onAdd={onAddLink}
          />

          {classroomEnabled && (
            <StudentClassroomPicker
              opened={classroomPickerOpened}
              onClose={() => setClassroomPickerOpened(false)}
              studentTaskId={data.identifierId}
              studentTaskDeliveryId={data.studentTaskDeliveryId}
              existingUrls={[
                ...data.googleDriveFiles.map(file => file.url),
                ...links.map(link => link.url),
              ]}
              onAttachLink={onAddLink}
              onAdded={onAutoSubmit}
            />
          )}
        </Stack>
      )}
    </Stack>
  )
}
